import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Product, ValidationError } from '../types';

interface ProductEntryTableProps {
  products: Product[];
  onChange: (products: Product[]) => void;
  errors: ValidationError[];
  onBulkImport: () => void;
}

const ProductEntryTable = ({ products, onChange, errors, onBulkImport }: ProductEntryTableProps) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());

  const formatNCM = (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 4) return numbers;
    return `${numbers.slice(0, 4)}.${numbers.slice(4, 6)}.${numbers.slice(6, 8)}`;
  };

  const formatCEST = (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 5) return `${numbers.slice(0, 2)}.${numbers.slice(2)}`;
    return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 7)}`;
  };

  const validateNCM = (ncm: string): boolean => {
    const numbers = ncm.replace(/\D/g, '');
    return numbers.length === 8;
  };

  const validateCEST = (cest: string): boolean => {
    const numbers = cest.replace(/\D/g, '');
    return numbers.length === 7;
  };

  const addNewProduct = () => {
    const newProduct: Product = {
      id: `product-${Date.now()}`,
      code: '',
      description: '',
      ncm: '',
      cest: '',
      quantity: 1
    };
    onChange([...products, newProduct]);
    setEditingId(newProduct.id);
  };

  const updateProduct = (id: string, field: keyof Product, value: string | number) => {
    const updated = products.map(p => {
      if (p.id === id) {
        if (field === 'ncm') {
          return { ...p, [field]: formatNCM(value as string) };
        }
        if (field === 'cest') {
          return { ...p, [field]: formatCEST(value as string) };
        }
        return { ...p, [field]: value };
      }
      return p;
    });
    onChange(updated);
  };

  const deleteProduct = (id: string) => {
    onChange(products.filter(p => p.id !== id));
    setSelectedProducts(prev => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  };

  const deleteSelected = () => {
    onChange(products.filter(p => !selectedProducts.has(p.id)));
    setSelectedProducts(new Set());
  };

  const toggleSelectProduct = (id: string) => {
    setSelectedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedProducts.size === products.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(products.map(p => p.id)));
    }
  };

  const getProductError = (productId: string, field: string): string | undefined => {
    return errors.find(e => e.field === `${productId}.${field}`)?.message;
  };

  const productLimit = 300;
  const remainingSlots = productLimit - products.length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Produtos</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {products.length} de {productLimit} produtos ({remainingSlots} restantes)
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {selectedProducts.size > 0 && (
            <Button
              variant="destructive"
              size="sm"
              iconName="Trash2"
              iconPosition="left"
              onClick={deleteSelected}
            >
              Excluir ({selectedProducts.size})
            </Button>
          )}
          
          <Button
            variant="outline"
            size="sm"
            iconName="Upload"
            iconPosition="left"
            onClick={onBulkImport}
          >
            Importar CSV/Excel
          </Button>
          
          <Button
            variant="default"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            onClick={addNewProduct}
            disabled={products.length >= productLimit}
          >
            Adicionar Produto
          </Button>
        </div>
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="w-12 px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={products.length > 0 && selectedProducts.size === products.length}
                    onChange={toggleSelectAll}
                    className="rounded border-border"
                    aria-label="Selecionar todos os produtos"
                  />
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Código</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Descrição</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">NCM</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">CEST</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Quantidade</th>
                <th className="w-24 px-4 py-3 text-center text-sm font-medium text-foreground">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-card">
              {products.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <Icon name="Package" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                    <p className="text-muted-foreground">Nenhum produto adicionado</p>
                    <p className="text-sm text-muted-foreground mt-1">Clique em "Adicionar Produto" para começar</p>
                  </td>
                </tr>
              ) : (
                products.map((product) => (
                  <tr key={product.id} className="hover:bg-muted/50 transition-colors">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedProducts.has(product.id)}
                        onChange={() => toggleSelectProduct(product.id)}
                        className="rounded border-border"
                        aria-label={`Selecionar produto ${product.code || 'sem código'}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <Input
                        type="text"
                        value={product.code}
                        onChange={(e) => updateProduct(product.id, 'code', e.target.value)}
                        placeholder="Código"
                        error={getProductError(product.id, 'code')}
                        className="min-w-[120px]"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <Input
                        type="text"
                        value={product.description}
                        onChange={(e) => updateProduct(product.id, 'description', e.target.value)}
                        placeholder="Descrição do produto"
                        error={getProductError(product.id, 'description')}
                        className="min-w-[200px]"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <Input
                        type="text"
                        value={product.ncm}
                        onChange={(e) => updateProduct(product.id, 'ncm', e.target.value)}
                        placeholder="0000.00.00"
                        error={getProductError(product.id, 'ncm')}
                        maxLength={10}
                        className="min-w-[120px] font-mono"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <Input
                        type="text"
                        value={product.cest}
                        onChange={(e) => updateProduct(product.id, 'cest', e.target.value)}
                        placeholder="00.000.00"
                        error={getProductError(product.id, 'cest')}
                        maxLength={9}
                        className="min-w-[120px] font-mono"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <Input
                        type="number"
                        value={product.quantity}
                        onChange={(e) => updateProduct(product.id, 'quantity', parseInt(e.target.value) || 0)}
                        min={1}
                        error={getProductError(product.id, 'quantity')}
                        className="min-w-[100px]"
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => deleteProduct(product.id)}
                        className="p-2 hover:bg-destructive/10 rounded-md transition-colors"
                        aria-label="Excluir produto"
                      >
                        <Icon name="Trash2" size={16} color="var(--color-destructive)" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {products.length >= productLimit && (
        <div className="flex items-center gap-2 p-4 bg-warning/10 border border-warning rounded-md">
          <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
          <p className="text-sm text-warning-foreground">
            Limite máximo de {productLimit} produtos atingido. Remova produtos para adicionar novos.
          </p>
        </div>
      )}
    </div>
  );
};

export default ProductEntryTable;